import { KButton, KDrawer, KMenuButton } from '@/ui-library/components';
import {
  DrawerFooter,
  DrawerHeader,
  Flex,
  Box,
  Button,
  Icon,
  Input,
  Spinner,
  MenuItem,
} from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { getVariantStyles } from '@/ui-library/components/molecules/KDrawer/KDrawer.styles';
import { KSteps } from '@/ui-library/components/molecules/KSteps';
import {
  OFFER_STEPS_APPROVAL,
  STEP_STATUS_COMPLETED,
  STEP_STATUS_ACTIVE,
  STEP_STATUS_PENDING,
  getOfferSteps,
} from './constants';
import { cloneDeep } from 'lodash-es';
import { OfferBodyContainer } from './OfferBodyContainer';
import { ChevronLeftIcon } from '@heroicons/react/solid';
import { Upload01, Mail02 } from '@kula-ai/icons-core';
import { Docusign, Pandadoc } from '@/assets/icons/esign';
import { Adobesign } from '@/assets/icons/ats';
import { useOfferStore } from './store';
import {
  useUploadAttachmentMutation,
  offerDetailsAPI,
  useGetApprovalSettingsQuery,
} from './api';
import {
  useSendWithDocuSignMutation,
  useSendWithPandaDocMutation,
  useSendWithAdobeMutation,
} from '@/store/query/offer-Initiation';
import { generateOfferPayload } from '@/components/ats/OfferInitiation/offerFormHelpers';
import { useShowToast } from '@/hooks';
import { getErrorMessage } from '@/utils/errors';

interface OfferContainerProps {
  onClose: () => void;
  applicationData: any;
  inComingOffer?: {
    id: number;
    status: string;
  };
  onSuccess: () => void;
  type: 'create' | 'edit';
  contact?: any;
  redirectToSendForApprovalTab?: boolean;
}

const OfferContainer = ({
  inComingOffer = null,
  onClose,
  applicationData,
  onSuccess,
  contact,
  type = 'create',
  redirectToSendForApprovalTab = false,
}: OfferContainerProps) => {
  const { data: approvalSettings, isFetching: getApprovalInProgress } =
    useGetApprovalSettingsQuery();

  // E-signature mutations
  const [sendWithDocuSign] = useSendWithDocuSignMutation();
  const [sendWithPandaDoc] = useSendWithPandaDocMutation();
  const [sendWithAdobe] = useSendWithAdobeMutation();

  const offerSteps = cloneDeep(OFFER_STEPS_APPROVAL);
  const [stepItems, setStepItems] = useState(offerSteps) as Array<any>;

  // Use selectors to subscribe to specific store values
  const addOfferAttachment = useOfferStore((state) => state.addOfferAttachment);
  const offerId = useOfferStore((state) => state.offerId);
  const setOfferId = useOfferStore((state) => state.setOfferId);
  const enableNextButton = useOfferStore((state) => state.enableNextButton);
  const setApplicationData = useOfferStore((state) => state.setApplicationData);
  const signatureType = useOfferStore((state) => state.signatureType);
  const setSignatureType = useOfferStore((state) => state.setSignatureType);
  const setIsApprovalEnabled = useOfferStore(
    (state) => state.setIsApprovalEnabled
  );
  const setIsReapprovalFieldsModified = useOfferStore(
    (state) => state.setIsReapprovalFieldsModified
  );
  const isReapprovalFieldsModified = useOfferStore(
    (state) => state.isReapprovalFieldsModified
  );
  const isApprovalEnabled = useOfferStore((state) => state.isApprovalEnabled);
  const setSkipApproval = useOfferStore((state) => state.setSkipApproval);
  const setCurrentOffer = useOfferStore((state) => state.setCurrentOffer);
  const hasValidTokens = useOfferStore((state) => state.hasValidTokens);
  const [createOrUpdateInitiated, setCreateOrUpdateInitiated] = useState(false);

  const currentStep = useMemo(() => {
    return stepItems.find((step) => step.status === STEP_STATUS_ACTIVE);
  }, [stepItems]);

  const [isCreationInProgress, setIsCreationInProgress] = useState(false);
  const [isDraftInProgress, setIsDraftInProgress] = useState(false);
  // candidate data
  const getCandidateName = useCallback(() => {
    return contact?.name ? 'for ' + contact?.name : '';
  }, [contact?.name]);

  const payload = useRef({
    offerDetails: {},
    offerLetterTemplate: {},
    offerApproval: {},
    offerEmail: {},
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useShowToast();
  const [isOfferCreated, setIsOfferCreated] = useState(false);

  useEffect(() => {
    if (!getApprovalInProgress) {
      const isApprovalEnabled =
        approvalSettings?.offer_approval_enabled || false;
      let updatedSteps = getOfferSteps(
        isApprovalEnabled,
        redirectToSendForApprovalTab
      );
      setStepItems(cloneDeep(updatedSteps));
      setIsApprovalEnabled(isApprovalEnabled);
    }
  }, [getApprovalInProgress]); // eslint-disable-line react-hooks/exhaustive-deps

  const setButtonDisabled = useMemo(() => {
    return (
      currentStep?.id === 'offerLetterTemplate' &&
      (!hasValidTokens || !enableNextButton)
    );
  }, [currentStep?.id, enableNextButton, hasValidTokens]);

  // child refs
  const offerDetailsRef = useRef(null);
  const offerLetterTemplateRef = useRef(null);
  const offerApprovalRef = useRef(null);
  const offerEmailRef = useRef(null);
  const childRefs = useMemo(
    () => ({
      OFFER_DETAILS_COMP: offerDetailsRef,
      OFFER_LETTER_TEMPLATE_COMP: offerLetterTemplateRef,
      OFFER_APPROVAL_COMP: offerApprovalRef,
      OFFER_EMAIL_COMP: offerEmailRef,
    }),
    []
  );

  useEffect(() => {
    // for edit case, we will get offer details
    if (inComingOffer && inComingOffer?.id) {
      setOfferId(inComingOffer?.id);
    } else {
      setOfferId(0);
    }
    // Set application data in store
    setApplicationData(applicationData);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // business methods
  const validateAndInitiateNextStep = async () => {
    // validate current step and show errors if any
    // else change current step to completed and next step to active
    const hasValidData = await currentStepHasErrors(currentStep);
    if (!hasValidData) return;

    const doneWithAPIContract = await initiateAPIContract();
    if (!doneWithAPIContract) {
      return;
    }

    const nextStep = currentStep?.nextNode;
    if (!nextStep) {
      return;
    }
    const nextStepItem = stepItems.find((item) => item.id === nextStep);

    const updatedStepItems = stepItems.map((item) => {
      if (item.id === currentStep?.id) {
        return { ...item, status: STEP_STATUS_COMPLETED };
      }
      if (item.id === nextStepItem?.id) {
        return { ...item, status: STEP_STATUS_ACTIVE, enabled: true };
      }
      return item;
    });

    setStepItems(updatedStepItems);
  }; // eslint-disable-line react-hooks/exhaustive-deps

  const initiateKStepSelection = (selectedItem) => {
    let selectedIndex = selectedItem['index'];
    let { status } = selectedItem['step'];
    if (status === STEP_STATUS_ACTIVE) return;
    if (status === STEP_STATUS_PENDING) {
      initiateKStepPendingClick(selectedItem['step']);
      return;
    }
    const updatedStepItems = stepItems.map((item, index) => {
      if (index === selectedIndex) {
        return { ...item, status: STEP_STATUS_ACTIVE };
      } else if (index > selectedIndex) {
        return { ...item, status: STEP_STATUS_PENDING, enabled: false };
      } else {
        return item;
      }
    });
    setApplicationData(applicationData);
    setStepItems(updatedStepItems);
  };
  const initiatePreviousStep = () => {
    // change current step to pending and previous step to active
    const previousNode = currentStep?.prevNode;
    if (!previousNode) return;

    const previousStepItem = stepItems.find((item) => item.id === previousNode);

    const updatedStepItems = stepItems.map((item) => {
      if (item.id === currentStep?.id) {
        return { ...item, status: STEP_STATUS_PENDING, enabled: false };
      }
      if (item.id === previousStepItem?.id) {
        return { ...item, status: STEP_STATUS_ACTIVE };
      }
      return item;
    });

    setStepItems(updatedStepItems);
  };
  const initiateKStepPendingClick = async (selectedItem) => {
    // Find the currently active step to compare against later
    const currentActiveStep = stepItems.find(
      (item) => item.status === STEP_STATUS_ACTIVE
    );

    // Get the index of the step the user clicked on
    const selectedIndex = stepItems.findIndex(
      (item) => item.id === selectedItem.id
    );

    // Performance optimization: If the clicked step is already active, skip any updates
    if (currentActiveStep?.id === selectedItem.id) {
      return;
    }

    // Limit validation to steps up to and including the selected step to enforce sequential order
    const stepsToValidate = stepItems.slice(0, selectedIndex + 1);

    // Validate all steps up to the selected one asynchronously, ensuring we wait for all results
    const responses = await Promise.all(
      stepsToValidate.map(async (item) => {
        return await currentStepHasErrors(item); // Returns true if step is valid, false if invalid
      })
    );

    // Find the index of the first invalid step (if any); -1 means all steps are valid
    const failedIndex = responses.findIndex((response) => !response);

    // Determine the target step: move to the selected step if all prior steps are valid,
    // otherwise move to the first invalid step
    const targetIndex = failedIndex === -1 ? selectedIndex : failedIndex;

    // Performance optimization: If the target step is already active, skip the update
    // This prevents redundant re-renders (e.g., clicking Step 4 when Step 2 is active and invalid)
    if (
      currentActiveStep &&
      stepItems[targetIndex].id === currentActiveStep.id
    ) {
      return;
    }

    // Update the step statuses based on the target index
    const updatedStepItems = stepItems.map((item, index) => {
      if (index < targetIndex) {
        // All steps before the target are marked as completed
        return { ...item, status: STEP_STATUS_COMPLETED, enabled: true };
      } else if (index === targetIndex) {
        // The target step becomes active
        return { ...item, status: STEP_STATUS_ACTIVE, enabled: true };
      } else {
        // Steps after the target remain pending and disabled
        return { ...item, status: STEP_STATUS_PENDING, enabled: false };
      }
    });

    // Apply the updated step statuses to the state
    setStepItems(updatedStepItems);
  };
  const currentStepHasErrors = async (kStep) => {
    const componentRef = childRefs[`${kStep['componentRef']}`];
    const componentModel = await componentRef?.current?.getModel();
    if (componentModel?.hasValidData) {
      setPayload(kStep, componentModel?.payload);
    }
    return componentModel?.hasValidData;
  };
  const setPayload = (kStep = {}, updatedPayload = {}) => {
    let key = kStep['id'];
    payload.current = {
      ...payload.current,
      [key]: updatedPayload,
    };
  };

  /* eslint-disable */
  const skipApproval = useMemo(() => {
    const skipApprovalValue =
      type === 'edit' && isApprovalEnabled && !isReapprovalFieldsModified;
    setSkipApproval(skipApprovalValue);
    return skipApprovalValue;
  }, [
    type,
    isApprovalEnabled,
    isReapprovalFieldsModified,
    setSkipApproval,
    setIsReapprovalFieldsModified,
  ]);
  /* eslint-enable */

  const initiateAPIContract = async () => {
    setIsCreationInProgress(true);
    setCreateOrUpdateInitiated(true);
    let apiStatus = true;
    const currentStepID = currentStep?.id;
    if (currentStepID === 'offerDetails') {
      apiStatus = await initiateOfferCreationUpdation();
    }
    if (currentStepID === 'offerLetterTemplate') {
      apiStatus = await initiateOfferTemplateUpdation();
      if (skipApproval) {
        onSuccess?.();
      }
    }
    if (currentStepID === 'offerApproval') {
      apiStatus = await initiateOfferApprovalSubmission();
      if (apiStatus) {
        setIsCreationInProgress(false);
        onSuccess?.();
      }
    }
    if (currentStepID === 'offerEmail') {
      apiStatus = await initiateOfferEmailSubmission();
      if (apiStatus) {
        setIsCreationInProgress(false);
        onSuccess?.();
      }
    }

    setIsCreationInProgress(false);
    return apiStatus;
  };

  const initiateOfferCreationUpdation = async (
    triggerReference: string = 'default'
  ) => {
    const offerPayload = generateOfferPayload({
      values: payload.current?.offerDetails || {},
      applicationId: applicationData?.id,
    });

    let offerID = inComingOffer?.id || offerId;

    const apiToInvoke =
      type === 'create' && !isOfferCreated
        ? offerDetailsAPI.createOffer
        : offerDetailsAPI.updateOffer;

    const response = (await apiToInvoke({
      payload: offerPayload,
      id: offerID,
    })) as any;

    const hasErrors =
      Array.isArray(response?.errors) && response.errors.length > 0;

    if (hasErrors) {
      offerCreationErrorHandler(response, triggerReference);
      return false;
    }

    // Success flow
    if (triggerReference === 'draft') {
      toast({
        title: 'Offer draft saved successfully',
        status: 'success',
      });
    }

    if (type === 'create') {
      setIsOfferCreated(true);
    }

    if (type === 'edit' && isApprovalEnabled) {
      const reapprovalRequired = response?.data?.requires_preapproval;
      // When null, treat as reapproval required since offer is not complete yet
      const isModified =
        reapprovalRequired == null || Boolean(reapprovalRequired);
      setIsReapprovalFieldsModified(isModified);

      // let reapprovalRequired = response?.data?.requires_preapproval;
      // if (isApprovalEnabled && reapprovalRequired == null) {
      //   // it will be null for when offer isn't completed yet
      //   setIsReapprovalFieldsModified(true);
      // } else {
      //   if (isApprovalEnabled && reapprovalRequired) {
      //     setIsReapprovalFieldsModified(true);
      //   } else {
      //     setIsReapprovalFieldsModified(false);
      //   }
      // }
    }

    setOfferId(response?.data?.id);
    return true;
  };

  const offerCreationErrorHandler = (
    response: any,
    triggerReference: string
  ) => {
    const errorMessage = getErrorMessage(response, 'offers');
    toast({
      title:
        errorMessage ||
        `Failed to ${triggerReference === 'draft' ? 'draft' : 'create'} offer`,
      description:
        typeof response?.errors?.[0] === 'string'
          ? `Error code: ${response?.errors?.[0]}`
          : 'An error occurred',
      status: 'error',
    });
  };

  const initiateOfferTemplateUpdation = async () => {
    const offerPayload = generateOfferPayload({
      values: payload.current?.offerDetails || {},
      applicationId: applicationData?.id,
    });

    const templateData: any = payload.current?.offerLetterTemplate || {};

    const combinedPayload = {
      ...offerPayload,
      ats_offer: {
        ...offerPayload?.ats_offer,
        ats_offer_template_id: templateData?.ats_offer_template_id,
        ats_offer_template: templateData?.ats_offer_template,
      },
    };

    const response = (await offerDetailsAPI.updateOffer({
      payload: combinedPayload,
      id: offerId,
    })) as any;

    if (response?.errors?.length === 0) {
      setCurrentOffer(response?.data);
      return true;
    } else {
      const errorMessage = getErrorMessage(response, 'offers');
      toast({
        title: errorMessage || 'Failed to update offer',
        description:
          typeof response?.errors?.[0] === 'string'
            ? `Error code: ${response?.errors?.[0]}`
            : 'An error occurred',
        status: 'error',
      });
    }
    return false;
  };

  const handleTemplateSelect = useCallback(
    async (templateId: string, templateValue: any) => {
      const offerPayload = generateOfferPayload({
        values: payload.current?.offerDetails || {},
        applicationId: applicationData?.id,
      });

      const combinedPayload = {
        ...offerPayload,
        ats_offer: {
          ...offerPayload?.ats_offer,
          ats_offer_template_id: templateId,
          ats_offer_template: templateValue,
        },
      };

      const response = (await offerDetailsAPI.updateOffer({
        payload: combinedPayload,
        id: offerId,
      })) as any;

      if (response?.errors?.length === 0) {
        return true;
      }
      return false;
    },
    [offerId, applicationData?.id]
  );

  const initiateOfferApprovalSubmission = async () => {
    const approvalData: any = payload.current?.offerApproval || {};

    const response = (await offerDetailsAPI.sendForApproval({
      id: offerId,
      payload: {
        note: approvalData?.note || '',
      },
    })) as any;

    const hasErrors =
      Array.isArray(response?.errors) && response.errors.length > 0;

    if (!hasErrors) {
      return true;
    } else {
      const errorMessage = getErrorMessage(response, 'offers');
      toast({
        title: 'Unable to create the offer',
        description: errorMessage || `Failed to send for approval`,
        status: 'error',
      });
    }
    return false;
  };

  const initiateOfferEmailSubmission = async () => {
    const emailPayload: any = payload.current?.offerEmail || {};

    if (signatureType === 'kula') {
      const response = (await offerDetailsAPI.sendOfferEmail({
        id: offerId,
        payload: emailPayload,
      })) as any;

      if (response?.errors?.length === 0) {
        return true;
      }
      toast({
        title: 'Failed to send offer, please contact support@kula.ai',
        description: response?.errors?.[0]?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });

      return false;
    } else {
      const sendMutation =
        signatureType === 'pandadoc'
          ? sendWithPandaDoc
          : signatureType === 'adobe'
          ? sendWithAdobe
          : sendWithDocuSign;

      const response = await sendMutation({
        id: offerId,
        payload: emailPayload,
      });

      if ('error' in response) {
        toast({
          title: 'Unable to send offer',
          status: 'error',
        });
        return false;
      }

      toast({
        title: 'Offer sent',
        status: 'success',
      });
      return true;
    }
  };

  // styles
  const styles = getVariantStyles('default');

  const renderHeader = useCallback(() => {
    return (
      <Box position="relative">
        <Box display="flex" alignItems="center" gap="10px">
          <KText size="md-semi-bold" color="secondary-900" lineHeight="30px">
            {type === 'edit' ? 'Edit' : 'Create'} offer {getCandidateName()}
          </KText>
        </Box>
        <Box mt="12px">
          <KSteps steps={stepItems as any} onSelect={initiateKStepSelection} />
        </Box>
      </Box>
    );
  }, [getCandidateName, stepItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadMutation = useUploadAttachmentMutation({
    onSuccess: (data) => {
      if ((data?.data as any)?.attachments) {
        addOfferAttachment(data?.data?.attachments?.[0]);
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to upload file',
        description: error?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const getOfferId = useCallback(() => {
    return inComingOffer?.id || offerId || 0;
  }, [offerId, inComingOffer?.id]);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const file = files[0];
      const offerId = getOfferId();

      uploadMutation.mutate({ id: offerId, file });

      // Reset input value to allow selecting the same file again
      if (event.target) {
        event.target.value = '';
      }
    },
    [getOfferId, uploadMutation]
  );

  const AttachementInput = useCallback(() => {
    return (
      <Box position="relative" cursor="pointer">
        <Input
          ref={fileInputRef}
          type="file"
          onChange={handleFileChange}
          position="absolute"
          top="0"
          left="0"
          width="100%"
          height="100%"
          opacity={0}
          cursor="pointer"
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.odt" // todo: vimal_test::reuse the global types
          zIndex={1}
        />
        <Button
          mr="6px"
          textAlign={'center'}
          variant="tertiary"
          isLoading={uploadMutation.isPending}
          data-test-id="upload-offer-attachments-button"
          pointerEvents="none"
          cursor="pointer"
        >
          <Icon as={Upload01} mr="5px" height="18px" width="18px" />
          Add more files
        </Button>
      </Box>
    );
  }, [uploadMutation.isPending, handleFileChange]);

  const getNextButtonLabel = useCallback(() => {
    if (skipApproval && currentStep?.id === 'offerLetterTemplate') {
      return 'Save offer';
    }
    if (currentStep?.id === 'offerEmail' && !isApprovalEnabled) {
      const labelMap = {
        kula: 'Send offer with Kula',
        docusign: 'Send offer with DocuSign',
        pandadoc: 'Send offer with PandaDoc',
        adobe: 'Send offer with Adobe',
      };
      return labelMap[signatureType] || 'Send offer';
    }
    return currentStep?.submitButtonLabel || 'Next';
  }, [skipApproval, currentStep?.submitButtonLabel]); // eslint-disable-line react-hooks/exhaustive-deps

  const signatureOptions = useMemo(
    () => [
      {
        id: 'kula',
        label: 'Send with Kula',
        icon: Mail02,
      },
      {
        id: 'docusign',
        label: 'Send with DocuSign',
        icon: Docusign,
      },
      {
        id: 'pandadoc',
        label: 'Send with PandaDoc',
        icon: Pandadoc,
      },
      {
        id: 'adobe',
        label: 'Send with Adobe Sign',
        icon: Adobesign,
      },
    ],
    []
  );

  const handleSignatureTypeSelect = useCallback(
    (signatureTypeId: string) => {
      setSignatureType(signatureTypeId as any);
      validateAndInitiateNextStep();
    },
    [setSignatureType] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const showDraftButton = useMemo(() => {
    return (
      // currentStep?.id === 'offerLetterTemplate' ||
      currentStep?.id === 'offerDetails'
    );
  }, [currentStep?.id, isDraftInProgress]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveDraft = async () => {
    const hasValidData = await currentStepHasErrors(currentStep);
    if (!hasValidData) return;
    setCreateOrUpdateInitiated(true);
    setIsDraftInProgress(true);
    await initiateOfferCreationUpdation('draft');
    setIsDraftInProgress(false);
  };

  const renderFooter = useCallback(() => {
    return (
      <Flex
        className="schedule-all-footer-container"
        justifyContent={'space-between'}
        w="100%"
      >
        <Flex>
          {currentStep?.prevNode && (
            <Button
              variant="quaternary"
              onClick={initiatePreviousStep}
              data-test-id="secondary-cta-button"
              p="8px 12px !important"
            >
              <Icon as={ChevronLeftIcon} mr="5px" height="18px" width="18px" />
              {currentStep?.previousButtonLabel || 'Back'}
            </Button>
          )}
        </Flex>
        <Flex>
          {currentStep?.id === 'offerLetterTemplate' && AttachementInput()}
          {currentStep?.id === 'offerLetterTemplate' && !isApprovalEnabled ? (
            <KMenuButton
              variant="primary"
              isLoading={isCreationInProgress}
              isDisabled={setButtonDisabled}
              data-test-id="send-offer-button"
              dividerProps={{
                display: 'none',
              }}
              triggerContent={
                <Flex alignItems="center" gap="8px">
                  <Icon as={Mail02} />
                  Send offer
                </Flex>
              }
              menuContent={
                <>
                  {signatureOptions.map((option) => (
                    <MenuItem
                      key={option.id}
                      icon={<Icon as={option.icon} boxSize="20px" />}
                      onClick={() => {
                        setSignatureType(option.id as any);
                        validateAndInitiateNextStep();
                      }}
                      data-test-id={`send-with-${option.id}-button`}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </>
              }
            />
          ) : (
            <Box display="flex" gap="6px">
              {showDraftButton && (
                <KButton
                  variant="secondary"
                  onClick={handleSaveDraft}
                  isLoading={isDraftInProgress}
                  data-test-id="offer-save-draft-button"
                >
                  Save draft
                </KButton>
              )}
              <KButton
                variant="primary"
                onClick={validateAndInitiateNextStep}
                isLoading={isCreationInProgress}
                isDisabled={setButtonDisabled}
                data-test-id="primary-cta-button"
              >
                {getNextButtonLabel()}
              </KButton>
            </Box>
          )}
        </Flex>
      </Flex>
    );
    // eslint-disable-next-line
  }, [
    currentStep,
    uploadMutation.isPending,
    isCreationInProgress,
    isDraftInProgress,
    setButtonDisabled,
    signatureOptions,
    handleSignatureTypeSelect,
  ]);

  return (
    <KDrawer
      contentProps={{
        maxW: '760px',
        borderTopLeftRadius: 'xl',
      }}
      isOpen={true}
      onClose={() => {
        if (createOrUpdateInitiated) {
          onSuccess();
        }
        onClose();
      }}
      handleOutsideClose={() => {}}
      header={<></>}
      footer={<></>}
    >
      {getApprovalInProgress ? (
        <Flex justifyContent="center" alignItems="center" h="100%">
          <Spinner />
        </Flex>
      ) : (
        <Flex
          flexDirection="column"
          position="relative"
          h="100vh"
          className="offer-container"
          data-test-id="offer-container"
        >
          <DrawerHeader {...styles.header} p="18px" flexGrow={0} flexShrink={0}>
            {renderHeader()}
          </DrawerHeader>
          <Box
            p="18px"
            className="offer-content-container"
            data-test-id="offer-content-container"
            flexGrow={1}
            overflow="hidden"
          >
            <Box display="block" overflow="auto" h="100%">
              <OfferBodyContainer
                currentStep={currentStep}
                childRefs={childRefs}
                onTemplateSelect={handleTemplateSelect}
                type={type}
              />
            </Box>
          </Box>
          <DrawerFooter {...styles.footer} p="18px" flexGrow={0} flexShrink={0}>
            {renderFooter()}
          </DrawerFooter>
        </Flex>
      )}
    </KDrawer>
  );
};

export default OfferContainer;
